import React from "react";
import { FiAlertCircle, FiRefreshCw, FiLock } from "react-icons/fi";

/**
 * "The request failed."
 *
 * ⚠️ Always RED, and always offers Retry — except on 403, where retrying the
 * same call cannot possibly help and the button would just invite the admin to
 * hammer it. Paired with EmptyState (grey, never Retry) this gives one
 * unambiguous read:
 *
 *      red + Retry        ⇒ the request failed
 *      grey + prose       ⇒ it succeeded and returned nothing
 *
 * The server's own message is shown verbatim rather than replaced with a
 * friendly generic — an admin debugging a user's account needs the real reason.
 */
export function getErrorInfo(error) {
  const status = error?.response?.status ?? null;
  const serverMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (Array.isArray(error?.response?.data?.errors)
      ? error.response.data.errors.join(", ")
      : null);

  return {
    status,
    message: serverMessage || error?.message || "Something went wrong.",
    isForbidden: status === 403,
    isNotFound: status === 404,
  };
}

export default function ErrorState({
  error,
  title,
  onRetry,
  compact = false,
}) {
  const { status, message, isForbidden, isNotFound } = getErrorInfo(error);

  const heading =
    title ||
    (isForbidden
      ? "You don't have access to this user's data"
      : isNotFound
        ? "Not found"
        : "Couldn't load this");

  // Retrying a 403 or a 404 re-runs an identical request for an identical
  // answer. Only offer it where it can actually change something.
  const canRetry = Boolean(onRetry) && !isForbidden && !isNotFound;
  const Icon = isForbidden ? FiLock : FiAlertCircle;

  return (
    <div
      role="alert"
      className={`mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50/60 text-center ${compact ? "p-4" : "p-8"}`}
    >
      <Icon className="mx-auto text-red-500" size={compact ? 24 : 36} />
      <h3 className="mt-3 text-base font-semibold text-red-800">{heading}</h3>
      <p className="mt-1 text-sm text-red-700 break-words">{message}</p>

      {status != null && (
        <p className="mt-2 font-mono text-[11px] text-red-500">HTTP {status}</p>
      )}

      {canRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
        >
          <FiRefreshCw size={14} />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * The stale-data variant: rows are still on screen from a previous successful
 * load, and a refetch failed. Never blank out good data because a refresh
 * failed — show what we have and say the refresh broke.
 */
export function ErrorBanner({ error, onRetry, onDismiss }) {
  const { status, message } = getErrorInfo(error);

  return (
    <div
      role="alert"
      className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
    >
      <FiAlertCircle className="text-red-500 shrink-0" size={18} />
      <p className="flex-1 text-sm text-red-800">
        Couldn't refresh — showing the last loaded results.{" "}
        <span className="text-red-600">{message}</span>
        {status != null && (
          <span className="ml-2 font-mono text-[11px] text-red-500">
            HTTP {status}
          </span>
        )}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
        >
          Retry
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md px-2 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
